import { useCallback, useEffect, useRef, useState, type CSSProperties, type JSX } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, type Notice } from '../api.ts';
import { whenShort } from '../lib/people.ts';

/**
 * The bell: what has happened here while you were doing something else.
 *
 * It exists because email was not enough. A teacher was told by letter that a pupil had run out of
 * quiz attempts; the letter was accepted for delivery, and she still reported that nothing had
 * arrived — she was looking at the school, not at her inbox. A notice that lives only in email
 * depends on somebody else's spam rules to reach the person who needs it.
 *
 * One component for both cabinets, like the envelope beside it. A pupil is told their work came
 * back; a teacher that somebody is stuck. The server decides which notices exist for whom, and
 * this only ever asks for its own.
 */

/** Slow on purpose: news half a minute old is not stale, and a poll per second is waste. */
const POLL_MS = 30_000;

export function Bell(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [unread, setUnread] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  /*
   * Where the panel hangs on a narrow screen, measured rather than assumed.
   *
   * On a wide one it hangs off the button and needs no help. On a phone it has to span the screen
   * instead: anchored to the button it would start three hundred pixels left of a button that
   * sits ninety from the right edge, which puts a third of it off the screen. Spanning the screen
   * means it can no longer take its vertical position from the button either — hence the measure,
   * which also copes with the bar being one row for a pupil and two for a teacher.
   */
  const [top, setTop] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  const read = useCallback((): void => {
    // A failed poll leaves the last known number alone. Flashing to nought on a dropped
    // connection would say "nothing happened" — the one thing a badge must never say wrongly.
    void api
      .notifications()
      .then((data) => {
        setNotices(data.notices);
        setUnread(data.unread);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    read();
    const timer = setInterval(read, POLL_MS);
    return () => clearInterval(timer);
    // Re-reads on navigation too, which is what makes a notice acted upon disappear promptly.
  }, [read, location.pathname]);

  // Clicking anywhere else closes it. A panel that can only be dismissed by finding the same
  // small button again is a panel people leave open and stop seeing.
  useEffect(() => {
    if (!open) return undefined;

    const onDown = (event: MouseEvent): void => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function toggle(): void {
    const next = !open;
    setOpen(next);

    if (next) setTop(Math.round(button.current?.getBoundingClientRect().bottom ?? 0) + 8);

    if (!next || unread === 0) return;

    /*
     * Opening is what marks them read. The badge clears at once rather than waiting for the
     * server: it has been seen, and a number still sitting there while somebody reads the list is
     * the interface arguing with them.
     */
    setUnread(0);
    void api.readNotifications().catch(() => undefined);
  }

  function follow(notice: Notice): void {
    setOpen(false);
    if (notice.link) void navigate(notice.link);
  }

  return (
    <div className="bell" ref={box}>
      <button
        ref={button}
        type="button"
        className="bell__button"
        onClick={toggle}
        aria-expanded={open}
        aria-label={unread > 0 ? `Уведомления: ${unread} новых` : 'Уведомления'}
        title={unread > 0 ? 'Есть новые уведомления' : 'Уведомления'}
      >
        {/* Drawn rather than typed: an emoji bell is a different picture on every system. */}
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M18 15.5v-4.7a6 6 0 0 0-12 0v4.7l-1.5 2h15z" />
          <path d="M10 19.2a2.2 2.2 0 0 0 4 0" />
        </svg>
        {unread > 0 ? (
          // Past ninety-nine the exact number stops being information and becomes a wide badge.
          <span className="bell__count">{unread > 99 ? '99+' : unread}</span>
        ) : null}
      </button>

      {open ? (
        <div
          className="bell__panel"
          role="dialog"
          aria-label="Уведомления"
          style={{ '--bell-top': `${top}px` } as CSSProperties}
        >
          {notices.length === 0 ? (
            <p className="muted bell__empty">Пока ничего не произошло.</p>
          ) : null}

          {notices.map((notice) => (
            <button
              key={notice.id}
              type="button"
              className={`bell__item${notice.read ? '' : ' bell__item--new'}`}
              onClick={() => follow(notice)}
              // Without a link there is nowhere to go, so the line stays plain rather than
              // behaving like a control that leads somewhere.
              disabled={!notice.link}
            >
              <span className="bell__line">
                <span className="bell__title">{notice.title}</span>
                <span className="bell__when">{whenShort(notice.created_at)}</span>
              </span>
              {notice.body ? <span className="bell__body">{notice.body}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
